/**
 * showEmailDeliveryError — surface a participant-email delivery failure.
 *
 * Shared by the flows that send participant email and must NOT silently report
 * success when delivery fails (gap STB-009): the Create Test wizard launch and
 * the Test Monitoring reminder actions. The backend maps EmailDeliveryError to
 * a 502 whose message names the failed recipient(s); this renders that as an
 * error toast.
 *
 * Behaviour is driven by whether a fallback is supplied:
 *   - email-delivery failure  → toast titled `title`, description = the backend
 *     message (names the failed recipients). Returns true.
 *   - any other error WITH `fallbackDescription` → toast with that generic
 *     description (the action still failed, just not for a delivery reason).
 *     Returns false.
 *   - any other error WITHOUT `fallbackDescription` → no toast (caller handles
 *     non-delivery errors itself, e.g. the wizard just navigates away).
 *     Returns false.
 */
import { showToast } from "./toast";
import { isEmailDeliveryError } from "../services/apiClient";

export function showEmailDeliveryError(
  err: unknown,
  opts: { title: string; fallbackDescription?: string },
): boolean {
  if (isEmailDeliveryError(err)) {
    showToast({ variant: "error", title: opts.title, description: err.message });
    return true;
  }
  if (opts.fallbackDescription !== undefined) {
    showToast({ variant: "error", title: opts.title, description: opts.fallbackDescription });
  }
  return false;
}
